import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS
import {
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";

import "./List.scss";
import { localeText_pl } from "../../../../Common/AgGridHelpers/locale_pl";
import { columnDefs, defaultColDef } from "./columnDefinitions";
import GridLoadingIndicator from "../../../../Common/AgGridHelpers/GridLoadingIndicator";
import httpGet from "../../../../Common/Http/httpGet";
import { DateTime } from "luxon";
import { returnableCableReelListItemVM } from "./returnableCableReelListItemVM";
import FormButtons from "../../../../Common/EditForm/formButtons";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { helpPopover } from "./helpPopover";
import { PreventSubmitInputs } from "../../../../Common/PreventSubmitInputs";
import { useResizeDetector } from "react-resize-detector";
import VhMeasureDiv from "../../../../Common/AgGridHelpers/VhMeasureDiv";
import { useNavigate } from "react-router-dom";

export default function List(props: {
  onOk: (ids: number[]) => void;
  onCancel: () => void;
  onNoRows: () => void;
}) {
  const gridRef = useRef<any>();
  const navigate = useNavigate();

  // #region Grid resizing support

  const gridWraperRef = useRef<HTMLDivElement>(null); // the wrapper div for grid
  const vhMeasureRef = useRef<HTMLDivElement>(null); // the invisible element to measure the screen height
  const buttonWraperRef = useRef<HTMLDivElement>(null); // the wrapper div for the ok and cancel buttons
  const { height } = useResizeDetector({
    // refreshMode: "debounce",
    // refreshRate: 200,
    handleWidth: false,
    targetRef: vhMeasureRef,
  }); // watch the sice of the screen height div

  const minGridHeight = 300; // the grid won't be smaller than this
  const gridPixelsToSubstract = 16; // bottom margin to correct imprecise calculations
  let gridHeight = minGridHeight;
  if (!!gridWraperRef.current && !!buttonWraperRef.current && !!height) {
    gridHeight =
      height -
      gridPixelsToSubstract -
      gridWraperRef.current.getBoundingClientRect().top -
      buttonWraperRef.current.getBoundingClientRect().height;
    if (gridHeight < minGridHeight) gridHeight = minGridHeight;
  }

  // this will be called every time the width of grid changes, except the first time, when the compontent mounts
  const onGridWidthChange = useCallback(() => {
    const { api } = gridRef.current;
    if (api != null) {
      // setTimeout guards us against the warning: Warning: Cannot update a component (`HeaderCellComp`) while rendering a different component...
      setTimeout(() => api.sizeColumnsToFit(), 0);
    }
  }, []);

  // the second resize detector for width
  // note that it has different settings, i.e. it monitors the grid wrapper div and it triggers only on subsequent changes
  useResizeDetector({
    handleHeight: false,
    skipOnMount: true,
    refreshMode: "debounce",
    refreshRate: 500,
    onResize: onGridWidthChange,
    targetRef: gridWraperRef,
  });

  // #endregion

  const [dataObject, setDataObject] =
    useState<returnableCableReelListItemVM[] | null>(null);
  const [searchExpression, setSearchExpression] = useState<string>("");
  const [isAnythingSelected, setIsAnythingSelected] = useState<boolean>(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the list data should be loaded once when the component mounts
  useEffect(() => {
    const setData = async () => {
      try {
        let a = await httpGet<returnableCableReelListItemVM[]>(
          "/api/CableReels/ReturnRequest/ReturnableCableReels"
        );

        if (a.length == 0) {
          props.onNoRows();
          return;
        }

        // fill the JS native Date column with the date-only part of the date
        a.forEach((item) => {
          item.invoiceDateAsDateOnly = DateTime.fromISO(item.invoiceDate)
            .startOf("day")
            .toJSDate();
        });
        setDataObject(a);
      } catch (error) {
        console.error(error);
        navigate("/server-communication-error");
      }
    };

    setData();
  }, []);

  // if (dataObject.length === 0) return <Loading></Loading>;

  // quick filter, the input is a controlled input
  const onSearchExpressionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchExpression(e.target.value);
    const { api } = gridRef.current;
    if (api == null) {
      return;
    }
    api.setQuickFilter(e.target.value);
  };

  let onOk = () => {
    let selectedRows = gridRef.current?.api.getSelectedRows();
    let seletedIds = selectedRows.map(
      (x: returnableCableReelListItemVM) => x.id
    );
    props.onOk(seletedIds);
  };

  let onSelectionChanged = (event: SelectionChangedEvent) => {
    var rowCount = event.api.getSelectedNodes().length;
    setIsAnythingSelected(rowCount > 0);
  };

  return (
    <>
      <h1 className="border-bottom pb-2 mt-4 mb-4">
        Nowe zgłoszenie odbioru - wybór bębnów
      </h1>
      <PreventSubmitInputs/>
      <div className="-page-action-form mb-3">
        <div className="-page-actions"></div>
        <div className="-page-help">
          <OverlayTrigger
            trigger="click"
            rootClose
            placement="left"
            overlay={helpPopover}
          >
            <button
              className="btn btn-outline-secondary py-1 px-1 me-2"
              // style={{ marginTop: "8px" }}
              type="button"
              title="Kliknij aby uzyskać pomoc."
            >
              <i className="bi bi-info-circle-fill ps-2 me-2"></i>
            </button>
          </OverlayTrigger>
        </div>
        <div className="-page-search">
          <div className="input-group">
            <input
              className="form-control border-end-0"
              type="text"
              id="search"
              name="search"
              placeholder="znajdź..."
              value={searchExpression}
              onChange={onSearchExpressionChange}
            />
            <div className="input-group-text bg-white">
              <i className="bi bi-search"></i>
            </div>
          </div>
        </div>
      </div>
      <div
        className="ag-theme-alpine -page-grid"
        style={{ width: "100%", height: gridHeight + "px" }}
        ref={gridWraperRef}
      >
        <AgGridReact
          ref={gridRef}
          rowData={dataObject}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          rowSelection="multiple"
          animateRows={true}
          localeText={localeText_pl}
          suppressCellFocus={true}
          suppressRowClickSelection={true}
          loadingOverlayComponent={GridLoadingIndicator}
          onSelectionChanged={onSelectionChanged}
        ></AgGridReact>
      </div>

      <div className="row pt-2" ref={buttonWraperRef}>
        <div className="col-12 offset-md-2 col-md-10 offset-lg-4 col-lg-8">
          <FormButtons
            onSave={(e) => onOk()}
            onCancel={(e) => props.onCancel()}
            saveText="Dalej"
            isSubmitting={false}
            isOkDisabled={!isAnythingSelected}
            okButtonIconClass="bi-arrow-right-circle"
          ></FormButtons>
        </div>
      </div>
      <VhMeasureDiv ref={vhMeasureRef} />
    </>
  );
}

function onGridReady(params: any) {
  params.api.sizeColumnsToFit();
}
